import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import configs from '../config';

const PropelLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className="w-10 h-10">
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export function ForgotPasswordPage() {
  const { tenantSlug } = useParams();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useDocumentTitle('Forgot Password - Propel');
  const resolvedTenantSlug = (tenantSlug || '').trim().toLowerCase();
  const loginPath = resolvedTenantSlug ? `/${resolvedTenantSlug}/login` : '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (resolvedTenantSlug) {
        headers['X-Tenant-Subdomain'] = resolvedTenantSlug;
      }

      const response = await fetch(`${configs.BACKEND_URL}/api/user/forgot-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error || 'Failed to send reset email');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Link to="/" className="flex items-center space-x-2 mb-2">
            <PropelLogo />
          </Link>
          <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          {isSubmitted ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Check your email
              </h2>
              <p className="text-sm text-center text-gray-500 mb-6">
                If an account exists with <strong>{email}</strong>, we've sent a password reset link. The link will expire in 1 hour.
              </p>
              <Link
                to={loginPath}
                className="w-full flex justify-center py-2.5 px-4 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back to Sign In
              </Link>
            </>
          ) : (
            <>
              <Link
                to={loginPath}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Sign In
              </Link>

              <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Forgot your password?
              </h2>
              <p className="text-sm text-center text-gray-500 mb-8">
                Enter your email and we'll send you a reset link
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2.5 text-sm transition-colors"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Propel. All rights reserved.
        </p>
      </div>
    </div>
  );
}
